type Dimensions = { width: number; height: number };
type Point = { x: number; y: number };
export type Region = { left: number, top: number, width: number, height: number }

/**
 * Resize an image but keep it centered on the focal point (with zoom).
 */
export function resizeToFocalPoint (
  original: Dimensions,
  target: Dimensions,
  focalPoint: Point,
  zoom: number
): { width: number; height: number; region: Region } {
  const { width, height, factor } = getIntermediateDimensions(original, target, zoom)
  const region = getExtractionRegion(factor, focalPoint, target, { width, height }, zoom)
  return { width, height, region }
}

/**
 * Calculates the dimensions of the intermediate (resized) image.
 */
function getIntermediateDimensions (
  original: Dimensions,
  target: Dimensions,
  zoom: number
): { width: number; height: number; factor: number } {
  const hRatio = original.height / target.height
  const wRatio = original.width / target.width

  let factor: number
  let width: number
  let height: number

  if (hRatio < wRatio) {
    factor = hRatio
    height = Math.round(target.height * zoom)
    width = Math.round((original.width * zoom) / hRatio)
  } else {
    factor = wRatio
    width = Math.round(target.width * zoom)
    height = Math.round((original.height * zoom) / wRatio)
  }
  return { width, height, factor }
}

/**
 * Calculates the region to extract from the intermediate image.
 */
function getExtractionRegion (
  factor: number,
  focalPoint: Point,
  target: Dimensions,
  intermediate: Dimensions,
  zoom: number
): Region {
  const newXCenter = (focalPoint.x * zoom) / factor
  const newYCenter = (focalPoint.y * zoom) / factor

  const region: Region = {
    left: 0,
    top: 0,
    width: target.width,
    height: target.height
  }

  region.left = clamp(0, intermediate.width - target.width, Math.round(newXCenter - target.width / 2))
  region.top = clamp(0, intermediate.height - target.height, Math.round(newYCenter - target.height / 2))
  return region
}

/**
 * limit the input value to the specified min and max values.
 */
function clamp (min: number, max: number, input: number) {
  return Math.min(Math.max(min, input), max)
}
